import React from 'react';
import { Check, X } from 'react-bootstrap-icons';
import CareMapprIconButton from '../../../../../components/Button/CareMapprIconButton';
import { CareMapprTextarea } from '../../../../../components/Inputs/textarea';
import CareMapprTextField from '../../../../../components/Inputs/textField/CareMapprTextField';
import editPenOutlineIcon from '../../../../../../assets/img/button-icons/edit-pen-outline.svg';
import type { Medication } from '../../../../../../lib/transferObjects/Medication';
import type { MedSection } from '../../../hook/types';
import { IMPORTANT_PATIENT_INFORMATION } from '../../../constants';

type ImportantPatientInformationContentProps = {
  section: MedSection;
  canEditMedicationProfile: boolean;
  ipiEditing: boolean;
  ipiDraft: string;
  ipiTitleDraft: string;
  setIpiEditing: React.Dispatch<React.SetStateAction<boolean>>;
  setIpiDraft: React.Dispatch<React.SetStateAction<string>>;
  setIpiTitleDraft: React.Dispatch<React.SetStateAction<string>>;
  onEditMedicationToSection: (data: any, targetSection: string) => void;
};

const ImportantPatientInformationContent = ({
  section,
  canEditMedicationProfile,
  ipiEditing,
  ipiDraft,
  ipiTitleDraft,
  setIpiEditing,
  setIpiDraft,
  setIpiTitleDraft,
  onEditMedicationToSection,
}: ImportantPatientInformationContentProps) => {
  if (section.medSectionBreak !== IMPORTANT_PATIENT_INFORMATION) return null;

  const medications = Array.isArray(section.medications)
    ? section.medications
    : [];
  const note = medications.find(
    (medication: Partial<Medication>) =>
      typeof medication?.furtherInformation === 'string',
  );
  const text = note?.furtherInformation;
  const title = typeof note?.medName === 'string' ? note.medName.trim() : '';

  if (!text) return null;

  if (canEditMedicationProfile && ipiEditing) {
    return (
      <div
        className="my-3 px-3 d-flex align-items-end gap-2"
        style={{ width: '50vw' }}
      >
        <div style={{ flex: 1 }}>
          <CareMapprTextField
            className="mb-2"
            value={ipiTitleDraft}
            onChange={(e: any) => setIpiTitleDraft(e.target.value)}
            placeholder="Title"
          />
          <CareMapprTextarea
            autoResize
            placeholder="Start typing..."
            controlId="edit-important-patient-information"
            rows={4}
            value={ipiDraft}
            onChange={(e: any) => setIpiDraft(e.target.value)}
          />
        </div>
        <CareMapprIconButton
          className="mb-0"
          icon={<X size={18} />}
          onClick={(e) => {
            e.preventDefault();
            setIpiEditing(false);
            setIpiDraft('');
            setIpiTitleDraft('');
          }}
        />
        <CareMapprIconButton
          className="mb-0"
          icon={<Check size={18} />}
          onClick={(e) => {
            e.preventDefault();
            if (!note) return;
            onEditMedicationToSection(
              {
                ...(note as any),
                medName: ipiTitleDraft,
                furtherInformation: ipiDraft,
              },
              section.medSectionBreak,
            );
            setIpiEditing(false);
            setIpiDraft('');
            setIpiTitleDraft('');
          }}
          disabled={!ipiTitleDraft.trim() || !ipiDraft.trim()}
        />
      </div>
    );
  }

  return (
    <div className="my-3 px-3 d-flex align-items-center gap-2">
      <div style={{ whiteSpace: 'pre-wrap', flex: 1 }}>
        {title ? (
          <div style={{ fontWeight: 600, fontSize: '16px' }}>{title}</div>
        ) : null}
        <div>{text}</div>
      </div>
      {canEditMedicationProfile ? (
        <button
          className="medication-button"
          onClick={(e) => {
            e.preventDefault();
            setIpiDraft(text);
            setIpiTitleDraft(title);
            setIpiEditing(true);
          }}
        >
          <img src={editPenOutlineIcon} className="icon" alt="edit pen icon" />
        </button>
      ) : null}
    </div>
  );
};

export default ImportantPatientInformationContent;
